import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { createTranslator } from '../../i18n';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const MAX_OPTIONS_VISIBLE = 3;

export interface OutboundOptionsCardProps {
  bodyText: string;
  options: string[];
  isOutgoing?: boolean;
  /** When true, only render the card (body). Options list is rendered by parent as sibling. */
  cardOnly?: boolean;
  headerText?: string;
  footerText?: string;
}

export const OutboundOptionsCard: React.FC<OutboundOptionsCardProps> = ({
  bodyText,
  options,
  isOutgoing = true,
  cardOnly = false,
  headerText: headerTextProp,
  footerText: footerTextProp,
}) => {
  const styles = useThemedStyles(getStyles);
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const textStyles = isOutgoing ? styles.textOutgoing : styles.textIncoming;
  const hasStructuredContent = !!(headerTextProp || footerTextProp);

  return (
    <View style={styles.wrapper}>
      <View style={[styles.card, isOutgoing ? styles.cardOutgoing : styles.cardIncoming]}>
        {!cardOnly ? (
          <View style={styles.header}>
            <Ionicons name="options-outline" size={18} color={isOutgoing ? chat.out.accent : colors.primary} />
            <Text style={[styles.badge, textStyles]}>{t('chat.optionsSent')}</Text>
          </View>
        ) : null}
        {hasStructuredContent ? (
          <>
            {headerTextProp ? (
              <Text style={[styles.cardHeader, textStyles]}>{headerTextProp}</Text>
            ) : null}
            {bodyText ? (
              <Text style={[styles.body, textStyles]}>{bodyText}</Text>
            ) : null}
            {footerTextProp ? (
              <Text style={[styles.cardFooter, textStyles]} numberOfLines={1} ellipsizeMode="tail">
                {footerTextProp}
              </Text>
            ) : null}
          </>
        ) : bodyText ? (
          <Text style={[styles.body, textStyles]}>{bodyText}</Text>
        ) : null}
      </View>
      {!cardOnly && options.length > 0 ? (
        <View style={styles.optionsListOuter}>
          {options.map((label, index) => (
            <View key={index} style={[styles.optionRow, isOutgoing ? styles.optionRowOutgoing : styles.optionRowIncoming]}>
              <Text style={[styles.optionLabel, styles.optionLabelCentered, isOutgoing ? styles.actionTextOutgoing : styles.actionTextIncoming]}>{label}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    width: '100%',
    alignSelf: 'stretch',
  },
  card: {
    borderRadius: 16,
    paddingVertical: spacing.sm,
    paddingHorizontal: 12,
    maxWidth: '100%',
  },
  cardOutgoing: {
    backgroundColor: chat.out.bg,
  },
  cardIncoming: {
    backgroundColor: colors.background.secondary,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  badge: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    opacity: 0.95,
  },
  cardHeader: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600',
    marginBottom: spacing.xs,
    alignSelf: 'flex-start',
  },
  body: {
    fontSize: typography.fontSize.sm,
    opacity: 0.95,
    marginBottom: spacing.xs / 2,
  },
  cardFooter: {
    fontSize: typography.fontSize.xs,
    opacity: 0.85,
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  optionsListOuter: {
    width: '100%',
    marginTop: spacing.sm,
    alignSelf: 'stretch',
    ...(Platform.OS === 'web' && { boxSizing: 'border-box' as any }),
  },
  optionsListOuterCompact: {
    marginTop: 0,
  },
  optionRow: {
    width: '100%',
    maxWidth: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    marginBottom: spacing.xs,
    ...(Platform.OS === 'web' && { boxSizing: 'border-box' as any }),
  },
  optionRowOutgoing: {
    backgroundColor: chat.out.chip,
  },
  optionRowIncoming: {
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  optionLabel: {
    fontSize: typography.fontSize.sm,
  },
  optionLabelCentered: {
    textAlign: 'center',
  },
  showOptionsButton: {
    width: '100%',
    maxWidth: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    ...(Platform.OS === 'web' && { boxSizing: 'border-box' as any }),
  },
  showOptionsButtonOutgoing: {
    backgroundColor: chat.out.chip,
  },
  showOptionsButtonIncoming: {
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  showOptionsButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  showOptionsButtonCollapse: {
    marginTop: spacing.sm,
  },
  textOutgoing: {
    color: chat.out.text,
  },
  textIncoming: {
    color: colors.text.primary,
  },
  actionTextOutgoing: {
    color: chat.out.accent,
  },
  actionTextIncoming: {
    color: colors.text.primary,
  },
});

/** Options list only – render after the card container, takes parent width. */
export interface OutboundOptionsListProps {
  options: string[];
  isOutgoing?: boolean;
  /** When true, no top margin (parent controls gap, e.g. same as option row gap). */
  compactTopMargin?: boolean;
  width?: number;
}

export const OutboundOptionsList: React.FC<OutboundOptionsListProps> = ({
  options,
  isOutgoing = true,
  compactTopMargin = false,
  width,
}) => {
  const styles = useThemedStyles(getStyles);
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const [expanded, setExpanded] = useState(false);
  const actionTextStyles = isOutgoing ? styles.actionTextOutgoing : styles.actionTextIncoming;
  if (!options.length) return null;

  const showToggle = options.length > MAX_OPTIONS_VISIBLE;
  const isCollapsed = showToggle && !expanded;

  return (
    <View style={[
      styles.optionsListOuter,
      width ? { width, maxWidth: width } : undefined,
      compactTopMargin && styles.optionsListOuterCompact
    ]}>
      {showToggle && isCollapsed ? (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => setExpanded(true)}
          style={[styles.showOptionsButton, isOutgoing ? styles.showOptionsButtonOutgoing : styles.showOptionsButtonIncoming]}
        >
          <Text style={[styles.showOptionsButtonText, actionTextStyles]}>{t('chat.showOptions')}</Text>
        </TouchableOpacity>
      ) : (
        options.map((label, index) => (
          <View key={index} style={[styles.optionRow, isOutgoing ? styles.optionRowOutgoing : styles.optionRowIncoming]}>
            <Text style={[styles.optionLabel, styles.optionLabelCentered, actionTextStyles]}>{label}</Text>
          </View>
        ))
      )}
      {showToggle && expanded ? (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => setExpanded(false)}
          style={[styles.showOptionsButton, styles.showOptionsButtonCollapse, isOutgoing ? styles.showOptionsButtonOutgoing : styles.showOptionsButtonIncoming]}
        >
          <Text style={[styles.showOptionsButtonText, actionTextStyles]}>{t('chat.hideOptions')}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
};
